import React, { useEffect, useRef } from 'react';
import cx from 'classnames';
import { PlacementType } from './types';
import useTooltipPositioning from './hooks/useTooltipPositioning';
import { getArrowStyle } from './Utils/getArrowStyle';
import styles from './CustomTooltip.module.scss';

interface Props {
  // Main text content of the tooltip
  text?: string;
  // Optional control for visibility
  isOpen?: boolean;
  // Placement of the tooltip relative to anchor
  placement?: PlacementType;
  // Visual style variant
  variant?: 'black' | 'teal' | 'orange';
  // Whether to use tag styling
  isTagStyle?: boolean;
  // Reference to the element the tooltip should be positioned relative to
  anchorRef?: React.RefObject<HTMLElement>;
  // Explicit position for the tooltip when not using anchorRef
  absolutePosition?: {
    top: string;
    left: string;
  };
  // Additional CSS class
  className?: string;
  // Whether to show the arrow
  arrow?: boolean;
  // Container reference for positioning context
  containerRef?: React.RefObject<HTMLElement>;
}

const CustomTooltip: React.FC<Props> = ({
  text,
  isOpen = true,
  placement = 'left',
  variant = 'black',
  isTagStyle = false,
  anchorRef,
  absolutePosition,
  className,
  arrow = true,
  containerRef,
}) => {
  // Component should at least have a ref or absolute position
  if (!anchorRef && !absolutePosition) {
    return null;
  }

  const tooltipRef = useRef<HTMLDivElement>(null);
  const { arrowPosition, tooltipStyle: tooltipPosition, updatePosition } = useTooltipPositioning(
    tooltipRef,
    anchorRef,
    placement,
    containerRef,
    absolutePosition
  );

  // Update position when dependencies change and set up listeners
  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);
      
      // Set up mutation observer to detect DOM changes that might affect positioning
      const observer = new MutationObserver(updatePosition);
      if (anchorRef?.current) {
        observer.observe(anchorRef.current, {
          attributes: true,
          childList: true,
          subtree: true
        });
      }
      
      // Also observe the tooltip element itself
      if (tooltipRef.current) {
        observer.observe(tooltipRef.current, {
          attributes: true,
          childList: true,
          subtree: true
        });
      }
      
      // Observe container changes as well if provided
      if (containerRef?.current) {
        observer.observe(containerRef.current, {
          attributes: true,
          childList: true,
          subtree: true
        });
      }
      
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
        observer.disconnect();
      };
    }
  }, [isOpen, updatePosition, anchorRef, containerRef]);

  // Don't render if tooltip is closed
  if (!isOpen) {
    return null;
  }

  // Build class names for tooltip
  const tooltipClasses = cx({
    [styles.tooltip]: !isTagStyle,
    [styles.tag]: isTagStyle,
    [styles[variant]]: true,
  });

  return (
    <div
      data-testid="tooltip"
      ref={tooltipRef}
      className={cx(tooltipClasses, className)}
      style={tooltipPosition}
    >
      {/* Arrow indicator */}
      {arrow && !isTagStyle && (
        <div
          className={styles.arrow}
          style={getArrowStyle(placement, arrowPosition, variant)}
        />
      )}
      {/* Tooltip text content */}
      {text && (
        <div className={styles.tooltipText}>
          {text}
        </div>
      )}
    </div>
  );
};

export default CustomTooltip; 